import { useState, useEffect, useMemo } from 'react';
import type { Tech, Ticket, TechWorkStatus as TechWorkStatusType } from '../../types/ticket';
import { TechWorkStatus } from '../../types/ticket';
import { TicketDetailDrawer } from '../../components/TicketDetailDrawer';
import { useTicketRepository, useTechRepository } from '../../data/hooks';
import './TechStatusView.css';

interface TechStatusViewProps {
  currentUserId: string;
}

// Calculate working time from start time
const formatWorkingTime = (startTime: Date | undefined): string => {
  if (!startTime) return '-';
  const now = new Date();
  const diffMs = now.getTime() - startTime.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;

  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

// Status color and icon mapping
const getStatusConfig = (status: TechWorkStatusType | undefined) => {
  switch (status) {
    case TechWorkStatus.WORKING:
      return {
        color: '#107c10',
        bgColor: 'rgba(16, 124, 16, 0.1)',
        label: 'Working',
        icon: (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="8" r="4" />
          </svg>
        )
      };
    case TechWorkStatus.AVAILABLE:
      return {
        color: '#0078d4',
        bgColor: 'rgba(0, 120, 212, 0.1)',
        label: 'Available',
        icon: (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 8l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )
      };
    case TechWorkStatus.BREAK:
      return {
        color: '#ca5010',
        bgColor: 'rgba(202, 80, 16, 0.1)',
        label: 'Break',
        icon: (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 2a6 6 0 100 12A6 6 0 008 2zm0 10.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9z"/>
            <path d="M8 5v3l2 2"/>
          </svg>
        )
      };
    case TechWorkStatus.LUNCH:
      return {
        color: '#8764b8',
        bgColor: 'rgba(135, 100, 184, 0.1)',
        label: 'Lunch',
        icon: (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M3 1v6c0 1.1.9 2 2 2h1v5a1 1 0 102 0V9h1c1.1 0 2-.9 2-2V1M12 1v14"/>
          </svg>
        )
      };
    case TechWorkStatus.OFFLINE:
    default:
      return {
        color: '#605e5c',
        bgColor: 'rgba(96, 94, 92, 0.1)',
        label: 'Offline',
        icon: (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="8" cy="8" r="4" opacity="0.4" />
          </svg>
        )
      };
  }
};

export const TechStatusView: React.FC<TechStatusViewProps> = ({ currentUserId }) => {
  const ticketRepo = useTicketRepository();
  const techRepo = useTechRepository();

  const [techs, setTechs] = useState<Tech[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [, setForceUpdate] = useState(0);

  // Load data from repositories
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const [loadedTechs, loadedTickets] = await Promise.all([
          techRepo.getAll(),
          ticketRepo.getAll(),
        ]);
        if (mounted) {
          setTechs(loadedTechs);
          setTickets(loadedTickets);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load tech status data:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [ticketRepo, techRepo]);

  // Update working time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setForceUpdate((prev) => prev + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Get ticket for a tech
  const getTicketForTech = (ticketId: string | undefined): Ticket | undefined => {
    if (!ticketId) return undefined;
    return tickets.find((t) => t.id === ticketId);
  };

  // Get open job count for a tech
  const getOpenJobCount = (techId: string): number => {
    return tickets.filter((t) => t.assignedTechId === techId && t.status !== 'Resolved').length;
  };

  // Handle clicking on current job
  const handleJobClick = (ticket: Ticket | undefined) => {
    if (ticket) {
      setSelectedTicket(ticket);
      setSidePanelOpen(true);
    }
  };

  // Sort techs: Working first, then Available, then Break/Lunch, then Offline
  const sortedTechs = useMemo(() => {
    const statusOrder: Record<TechWorkStatusType, number> = {
      [TechWorkStatus.WORKING]: 0,
      [TechWorkStatus.AVAILABLE]: 1,
      [TechWorkStatus.BREAK]: 2,
      [TechWorkStatus.LUNCH]: 3,
      [TechWorkStatus.OFFLINE]: 4,
    };

    return [...techs].sort((a, b) => {
      const orderA = statusOrder[a.workStatus || TechWorkStatus.OFFLINE];
      const orderB = statusOrder[b.workStatus || TechWorkStatus.OFFLINE];
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
    });
  }, [techs]);

  // Count by status
  const statusCounts = useMemo(() => {
    return {
      working: techs.filter((t) => t.workStatus === TechWorkStatus.WORKING).length,
      available: techs.filter((t) => t.workStatus === TechWorkStatus.AVAILABLE).length,
      break: techs.filter((t) => t.workStatus === TechWorkStatus.BREAK).length,
      lunch: techs.filter((t) => t.workStatus === TechWorkStatus.LUNCH).length,
      offline: techs.filter((t) => t.workStatus === TechWorkStatus.OFFLINE).length,
    };
  }, [techs]);

  if (isLoading) {
    return null;
  }

  return (
    <div className="tech-status-view">
      {/* Summary Cards */}
      <div className="tech-status-view__summary">
        <div className="tech-status-view__summary-card tech-status-view__summary-card--working">
          <div className="tech-status-view__summary-icon">
            <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="8" cy="8" r="4" />
            </svg>
          </div>
          <div className="tech-status-view__summary-content">
            <span className="tech-status-view__summary-value">{statusCounts.working}</span>
            <span className="tech-status-view__summary-label">Working</span>
          </div>
        </div>
        <div className="tech-status-view__summary-card tech-status-view__summary-card--available">
          <div className="tech-status-view__summary-icon">
            <svg width="24" height="24" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 8l3 3 5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="tech-status-view__summary-content">
            <span className="tech-status-view__summary-value">{statusCounts.available}</span>
            <span className="tech-status-view__summary-label">Available</span>
          </div>
        </div>
        <div className="tech-status-view__summary-card tech-status-view__summary-card--away">
          <div className="tech-status-view__summary-icon">
            <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 2a6 6 0 100 12A6 6 0 008 2zm0 10.5a4.5 4.5 0 110-9 4.5 4.5 0 010 9z"/>
            </svg>
          </div>
          <div className="tech-status-view__summary-content">
            <span className="tech-status-view__summary-value">{statusCounts.break + statusCounts.lunch}</span>
            <span className="tech-status-view__summary-label">Away</span>
          </div>
        </div>
        <div className="tech-status-view__summary-card tech-status-view__summary-card--offline">
          <div className="tech-status-view__summary-icon">
            <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="8" cy="8" r="4" opacity="0.4" />
            </svg>
          </div>
          <div className="tech-status-view__summary-content">
            <span className="tech-status-view__summary-value">{statusCounts.offline}</span>
            <span className="tech-status-view__summary-label">Offline</span>
          </div>
        </div>
      </div>

      {/* Tech Status Table */}
      <div className="tech-status-view__table-container">
        <table className="tech-status-view__table">
          <thead>
            <tr>
              <th>Tech</th>
              <th>Status</th>
              <th>Working Time</th>
              <th>Current Job</th>
              <th>Open Jobs</th>
              <th>Location</th>
            </tr>
          </thead>
          <tbody>
            {sortedTechs.map((tech) => {
              const statusConfig = getStatusConfig(tech.workStatus);
              const currentTicket = getTicketForTech(tech.currentTicketId);
              const openJobs = getOpenJobCount(tech.id);

              return (
                <tr key={tech.id} className={tech.workStatus === TechWorkStatus.OFFLINE ? 'tech-status-view__row--offline' : ''}>
                  <td>
                    <div className="tech-status-view__tech-cell">
                      <div
                        className="tech-status-view__avatar"
                        style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
                      >
                        {tech.name.split(' ').map((n) => n[0]).join('')}
                      </div>
                      <div className="tech-status-view__tech-info">
                        <span className="tech-status-view__tech-name">{tech.name}</span>
                        <span className="tech-status-view__tech-tier">{tech.tier}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div
                      className="tech-status-view__status-badge"
                      style={{ backgroundColor: statusConfig.bgColor, color: statusConfig.color }}
                    >
                      {statusConfig.icon}
                      <span>{statusConfig.label}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`tech-status-view__time ${
                      tech.workStatus === TechWorkStatus.WORKING ? 'tech-status-view__time--active' :
                      tech.workStatus === TechWorkStatus.AVAILABLE ? 'tech-status-view__time--available' :
                      tech.workStatus === TechWorkStatus.BREAK ? 'tech-status-view__time--break' :
                      tech.workStatus === TechWorkStatus.LUNCH ? 'tech-status-view__time--lunch' : ''
                    }`}>
                      {(tech.workStatus === TechWorkStatus.WORKING ||
                        tech.workStatus === TechWorkStatus.AVAILABLE ||
                        tech.workStatus === TechWorkStatus.BREAK ||
                        tech.workStatus === TechWorkStatus.LUNCH)
                        ? formatWorkingTime(tech.workStartTime)
                        : '-'
                      }
                    </span>
                  </td>
                  <td>
                    {currentTicket ? (
                      <button
                        className="tech-status-view__job-link"
                        onClick={() => handleJobClick(currentTicket)}
                      >
                        <span className="tech-status-view__job-number">{currentTicket.ticketNumber}</span>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ) : (
                      <span className="tech-status-view__no-job">-</span>
                    )}
                  </td>
                  <td>
                    <div className="tech-status-view__job-count">
                      <span className={`tech-status-view__job-count-value ${openJobs > 5 ? 'tech-status-view__job-count-value--high' : ''}`}>
                        {openJobs}
                      </span>
                      <span className="tech-status-view__job-count-max">/ {tech.maxLoad}</span>
                    </div>
                  </td>
                  <td>
                    <span className="tech-status-view__location">{tech.location}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Ticket Detail Drawer */}
      <TicketDetailDrawer
        open={sidePanelOpen}
        ticket={selectedTicket}
        tech={selectedTicket ? techs.find((t) => t.id === selectedTicket.assignedTechId) : undefined}
        onClose={() => {
          setSidePanelOpen(false);
          setSelectedTicket(null);
        }}
      />
    </div>
  );
};
